import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Bell, BellOff, Save, Loader2, Lock } from 'lucide-react';
import api from '../../utils/api';


function getAuthHeaders() {
  return { 'Content-Type': 'application/json' };
}

// TREATX-OWNER-UAT-NOTIF-POLICY-01 (2026-08): configurable event
// rows are now derived from `GET /api/notifications/policy`, which
// projects the canonical `event_consent_categories` partition
// against the caller's role. No hard-coded event list; no parallel
// preference authority. Mandatory (LEGAL_MUST_SEND) rows are shown
// as read-only so admins can see they exist without being able to
// disable them.

function _labelFor(eventKey, t) {
  // Prefer a matching i18n key when available; otherwise render the
  // event key in a friendly Title-Case form as a last resort. All
  // translations for known keys live under `notifSettings.event.*`
  // in the shared i18n bundle.
  const k = `notifSettings.event.${eventKey}`;
  const translated = t(k);
  if (translated && translated !== k) return translated;
  return eventKey
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export default function ClinicNotificationSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [policy, setPolicy] = useState({ mandatory: [], configurable: [] });
  const [dirty, setDirty] = useState(false);
  const [localEmail, setLocalEmail] = useState(true);
  const [localEvents, setLocalEvents] = useState({});

  const fetchPrefs = useCallback(async () => {
    try {
      const [prefsRes, policyRes] = await Promise.all([
        api.get(`/clinic/notifications/preferences`),
        api.get(`/notifications/policy`),
      ]);
      if (prefsRes.data?.success) {
        setPrefs(prefsRes.data);
        setLocalEmail(prefsRes.data.email_enabled);
        setLocalEvents({ ...(prefsRes.data.events || {}) });
      }
      if (policyRes.data?.success) {
        setPolicy({
          mandatory: policyRes.data.mandatory || [],
          configurable: policyRes.data.configurable || [],
        });
      }
    } catch {
      toast.error(t('provider.notifSettings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleToggleEmail = (val) => { setLocalEmail(val); setDirty(true); };
  const handleToggleEvent = (key, val) => { setLocalEvents(prev => ({ ...prev, [key]: val })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/clinic/notifications/preferences', {
        email_enabled: localEmail,
        events: localEvents,
      });
      const data = res.data;
      if (data.success) {
        setPrefs(data);
        setDirty(false);
        toast.success(t('provider.notifSettings.saved'));
      } else {
        toast.error(data.detail || t('provider.notifSettings.saveFailed'));
      }
    } catch {
      toast.error(t('provider.notifSettings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#3BA4C5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#8892a4] hover:text-white mb-6 transition-colors" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> {t('provider.common.back')}
        </button>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-[#3BA4C5]/10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-[#3BA4C5]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold" data-testid="page-title">{t('provider.notifSettings.title')}</h1>
            <p className="text-sm text-[#8892a4]">{t('provider.notifSettings.subtitle')}</p>
          </div>
        </div>

        <Card className="bg-[#162031] border-[#1e3048]">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-white flex items-center gap-2">
                {localEmail ? <Bell className="w-4 h-4 text-[#3BA4C5]" /> : <BellOff className="w-4 h-4 text-[#8892a4]" />}
                {t('provider.notifSettings.emailNotifications')}
              </CardTitle>
              <Switch checked={localEmail} onCheckedChange={handleToggleEmail} data-testid="master-toggle" />
            </div>
            <p className="text-sm text-[#8892a4] mt-1">
              {localEmail ? t('provider.notifSettings.emailOnDesc') : t('provider.notifSettings.emailOffDesc')}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Configurable (OPERATIONAL / MARKETING) — policy-derived */}
            {policy.configurable.map(({ event_key: key }) => (
              <div key={key} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${localEmail ? 'border-[#1e3048] bg-[#0B1120]/50' : 'border-[#1e3048]/50 opacity-50'}`} data-testid={`event-row-${key}`}>
                <div>
                  <Label className="text-sm font-medium text-white">{_labelFor(key, t)}</Label>
                </div>
                <Switch
                  checked={localEvents[key] ?? true}
                  onCheckedChange={(v) => handleToggleEvent(key, v)}
                  disabled={!localEmail}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}

            {/* Mandatory (LEGAL_MUST_SEND) — visible, not disableable */}
            {policy.mandatory.length > 0 && (
              <div className="pt-4 border-t border-[#1e3048]">
                <div className="text-xs uppercase tracking-wide text-[#8892a4] mb-2 flex items-center gap-1">
                  <Lock className="w-3 h-3" /> {t('provider.notifSettings.mandatoryHeader') || 'Mandatory security & compliance emails'}
                </div>
                {policy.mandatory.map(({ event_key: key }) => (
                  <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-[#1e3048]/60 bg-[#0B1120]/30 mb-2" data-testid={`mandatory-row-${key}`}>
                    <div>
                      <Label className="text-sm font-medium text-[#c5cce0]">{_labelFor(key, t)}</Label>
                      <p className="text-xs text-[#8892a4] mt-0.5">
                        {t('provider.notifSettings.mandatoryReason') || 'Required — cannot be disabled'}
                      </p>
                    </div>
                    <span className="text-xs text-[#3BA4C5] flex items-center gap-1"><Lock className="w-3 h-3" /> {t('provider.notifSettings.required') || 'Required'}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} disabled={!dirty || saving} className="bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white gap-2" data-testid="save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? t('provider.common.saving') : t('provider.common.saveChanges')}
          </Button>
        </div>

        {prefs?.source === 'custom' && prefs?.updated_at_utc && (
          <p className="text-xs text-[#8892a4] text-right mt-2" data-testid="last-updated">
            {t('provider.common.lastUpdated')}: {formatDateLocale(prefs.updated_at_utc)}
          </p>
        )}
      </div>
    </div>
  );
}
